import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const FeatureAccessIndicator = ({ 
  user, 
  feature = 'premium',
  showUpgradePrompt = true,
  inline = false,
  size = 'default', // 'sm' | 'default' | 'lg'
  className = ''
}) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const getSubscriptionStatus = () => {
    if (!user) return 'guest';
    if (user?.isPremium) return 'premium';
    return 'free';
  };

  const getFeatureAccess = (featureName) => {
    const status = getSubscriptionStatus();
    
    const featureMatrix = {
      'basic-assessment': ['guest', 'free', 'premium'],
      'advanced-assessment': ['premium'],
      'ai-plans': ['premium'],
      'progress-tracking': ['free', 'premium'],
      'exercise-management': ['free', 'premium'],
      'premium-exercises': ['premium'],
      'analytics': ['premium'],
      'export': ['premium'],
      'priority-support': ['premium']
    };

    return featureMatrix?.[featureName]?.includes(status) || false;
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          badge: 'px-2 py-1 text-xs',
          icon: 12,
          text: 'text-xs'
        };
      case 'lg':
        return {
          badge: 'px-4 py-2 text-base',
          icon: 20,
          text: 'text-base'
        };
      default:
        return {
          badge: 'px-3 py-1.5 text-sm',
          icon: 16,
          text: 'text-sm'
        };
    }
  };

  const sizeClasses = getSizeClasses();
  const status = getSubscriptionStatus();
  const hasAccess = getFeatureAccess(feature);

  // Status Badge Component
  const StatusBadge = () => {
    const badgeConfig = {
      guest: {
        icon: 'User',
        text: 'Guest',
        bgColor: 'bg-muted',
        textColor: 'text-muted-foreground',
        description: 'Limited access - Sign in for more features'
      },
      free: {
        icon: 'User',
        text: 'Free',
        bgColor: 'bg-secondary/10',
        textColor: 'text-secondary',
        description: 'Free account with basic features'
      },
      premium: {
        icon: 'Crown',
        text: 'Premium',
        bgColor: 'bg-warning/10',
        textColor: 'text-warning',
        description: 'Full access to all features'
      }
    };

    const config = badgeConfig?.[status];

    if (inline) {
      return (
        <span className={`inline-flex items-center space-x-1 ${sizeClasses?.badge} ${config?.bgColor} ${config?.textColor} rounded-full font-medium`}>
          <Icon name={config?.icon} size={sizeClasses?.icon} />
          <span>{config?.text}</span>
        </span>
      );
    }

    return (
      <div className={`flex items-center space-x-2 ${sizeClasses?.badge} ${config?.bgColor} ${config?.textColor} rounded-lg font-medium`}>
        <Icon name={config?.icon} size={sizeClasses?.icon} />
        <div className="flex flex-col">
          <span>{config?.text} Account</span>
          <span className={`${sizeClasses?.text} opacity-75 font-normal`}>
            {config?.description}
          </span>
        </div>
      </div>
    );
  };

  // Feature Access Indicator
  const FeatureAccess = () => {
    if (hasAccess) {
      return (
        <div className={`flex items-center space-x-2 ${sizeClasses?.text} text-success`}>
          <Icon name="CheckCircle" size={sizeClasses?.icon} />
          <span>Available</span>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className={`flex items-center space-x-2 ${sizeClasses?.text} text-muted-foreground`}>
          <Icon name="Lock" size={sizeClasses?.icon} />
          <span>
            {status === 'guest' ? 'Sign in required' : 'Premium required'}
          </span>
        </div>
        {showUpgradePrompt && (
          <div className="flex items-center space-x-2">
            {status === 'guest' ? (
              <Button
                variant="outline"
                size="sm"
                iconName="LogIn"
                iconPosition="left"
                asChild
              >
                <Link to="/authentication">Sign In</Link>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                iconName="Crown"
                iconPosition="left"
                onClick={() => setShowUpgradeModal(true)}
              >
                Upgrade
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Upgrade Modal
  const UpgradeModal = () => {
    if (!showUpgradeModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={() => setShowUpgradeModal(false)}
        />
        <div className="relative bg-card rounded-lg shadow-lg border border-border max-w-md w-full p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Upgrade to Premium</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowUpgradeModal(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Crown" size={32} className="text-warning" />
              </div>
              <p className="text-muted-foreground">
                Unlock advanced career insights, AI-powered recommendations, and personalized development plans.
              </p>
            </div>

            <div className="space-y-2">
              {[
                'AI-powered career plan generation',
                'Advanced progress analytics',
                'Premium exercise library',
                'Priority support',
                'Export capabilities'
              ]?.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <Icon name="Check" size={16} className="text-success" />
                  <span className="text-muted-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="Crown"
              iconPosition="left"
              asChild
            >
              <Link to="/subscription-management">View Plans</Link>
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      <div className="space-y-3">
        <StatusBadge />
        <FeatureAccess />
      </div>
      <UpgradeModal />
    </div>
  );
};

// Quick Access Component for Navigation
export const QuickAccessIndicator = ({ user, className = "" }) => {
  const status = user?.isPremium ? 'premium' : user ? 'free' : 'guest';
  
  const statusConfig = {
    guest: {
      icon: 'User',
      text: 'Guest',
      color: 'text-muted-foreground',
      bg: 'bg-muted'
    },
    free: {
      icon: 'User',
      text: 'Free',
      color: 'text-secondary',
      bg: 'bg-secondary/10'
    },
    premium: {
      icon: 'Crown',
      text: 'Premium',
      color: 'text-warning',
      bg: 'bg-warning/10'
    }
  };

  const config = statusConfig?.[status];

  return (
    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.color} ${className}`}>
      <Icon name={config?.icon} size={12} />
      <span>{config?.text}</span>
    </div>
  );
};

// Feature Lock Component
export const FeatureLock = ({ 
  feature, 
  user, 
  children, 
  fallback = null,
  className = ""
}) => {
  const status = user?.isPremium ? 'premium' : user ? 'free' : 'guest';
  
  const featureMatrix = {
    'ai-plans': ['premium'],
    'advanced-analytics': ['premium'],
    'premium-exercises': ['premium'],
    'export': ['premium'],
    'basic-features': ['free', 'premium'],
    'auth-required': ['free', 'premium']
  };

  const hasAccess = featureMatrix?.[feature]?.includes(status) || false;

  if (!hasAccess) {
    return fallback || (
      <div className={`opacity-50 pointer-events-none ${className}`}>
        {children}
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm rounded">
          <Icon name="Lock" size={20} className="text-muted-foreground" />
        </div>
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};

export default FeatureAccessIndicator;